import * as React from 'react';
import { ILike } from '../../interfaces/ILike';
import { IUser } from '../../interfaces/IUser';
import { IItem } from '../../interfaces/IItem';
import { IACardViewService } from './CardView.service';
import { IMessage } from '../../interfaces/IMessage';
import { IACard } from '../card/Card';
import DateTimeService from '../../services/DateTimeService';
import { IFontSlots } from '../../interfaces/IFontSlots';

export interface IMessagesCardViewProps {
  items: IItem[];
  width: number;
  numberOfCards: number;
  tenant: string;
  token: string;
  environment: "Development" | "Test" | "Production";
  instanceName: string;
  type: "Event" | "Message" | "ContentCreator" | "ContentOrganizer";
  user: IUser;
  userRole: "Reader" | "Editor" | "Administrator";
  likesEnabled: boolean;
  commentsEnabled: boolean;
  isMobile: boolean;
  isPanelOpen: boolean;

  titleColor: string;
  textColor: string;
  highlightColor: string;
  borderColor: string;
  borderRadius: number;
  backgroundColor: string;

  loading: boolean;
  cardPadding: number;
  minCardWidth: number;

  showImage: boolean;
  showTitle: boolean;
  showCategories: boolean;
  showTeaser: boolean;
  showAuthor: boolean;
  showStartDate: boolean;
  showLikes: boolean;
  showComments: boolean;
  showCommandBarItems: boolean;
  showDropShadow: boolean;
  showLocation?: boolean;
  showTimespand?: boolean;
  displayDateOnCover?: boolean;
  targetedTo?: any;
  showUniqueViews?: boolean;
  hasDarkBackground?: boolean;
  maxAmountOfColumns: number;
  unReadHighlight?: string;
  showLikesDimmed?: boolean;
  showCommentsDimmed?: boolean;
  showUniqueViewsDimmed?: boolean;
  hideCommandBarButton?: boolean;
  fontSlots?: IFontSlots;
  userLocation?: string;
  reactionOptions?: string[];
  showEmojiPickerExpandButton?: boolean;

  onClickEdit: (item: IItem) => void;
  onClickDelete: (item: IItem) => void;
  onClickOpen: (item: IItem) => void;
  onClickCopy: (item: IItem) => void;
  likesUpdated: (message: IMessage, likes: ILike[], index: number) => void;
  commentsUpdated: (message: IMessage, commentCount: number, index: number) => void;
  commentsClick: (message: IMessage) => void;
}

export default class IAMessagesCardView extends React.Component<IMessagesCardViewProps> {
  private cardViewService: IACardViewService = new IACardViewService();
  private dateTimeService = new DateTimeService();

  constructor(props, state) {
    super(props, state);
  }

  private skeletonCards(): JSX.Element[] {
    // Resizing cards
    const [numberOfCardsPerRow, maxCardWidthAvailable] = this.cardViewService.getNumberOfCardsPerRowAndMaxCardWidthAvailable(this.props.width, this.props.cardPadding, this.props.minCardWidth, this.props.maxAmountOfColumns);
    let currentRowNumber = 1;
    let skeletonCards = [];
    let numMessages = this.props.numberOfCards > 20 ? 20 : this.props.numberOfCards;
    for (let i = 0; i < numMessages; i++) {
      skeletonCards.push(
        <IACard
          key={i}
          showSkeleton={true}
          tags={this.props.showCategories ? [] : undefined}
          title={this.props.showTitle ? "Title" : undefined}
          teaser={this.props.showTeaser ? "Teaser" : undefined}
          author={this.props.showAuthor ? "Author" : undefined}
          date={this.props.showStartDate ? new Date() : undefined}
          cover={this.props.showImage ? {} : undefined}
          likes={this.props.showLikes ? [] : undefined}
          comments={this.props.showComments ? 0 : undefined}
          width={window.innerWidth < 667 ? window.innerWidth - 56 : maxCardWidthAvailable}
          isMobile={this.props.isMobile}
          isPanelOpen={this.props.isPanelOpen}
          design={{
            borderRadius: this.props.borderRadius
          }}
          styles={{
            marginRight: currentRowNumber == numberOfCardsPerRow ? 0 : 20,
            marginBottom: 20,
            width: window.innerWidth < 667 ? "100%" : maxCardWidthAvailable,
          }}
          onClickOpen={() => { }}
        />
      );
      if (currentRowNumber == numberOfCardsPerRow) {
        currentRowNumber = 1;
      } else {
        currentRowNumber++;
      }
    }
    return skeletonCards;
  }

  public render(): JSX.Element {
    let messagesToDisplay = [];
    if (this.props.items != undefined) {
      // Resizing cards
      const [numberOfCardsPerRow, maxCardWidthAvailable] = this.cardViewService.getNumberOfCardsPerRowAndMaxCardWidthAvailable(this.props.width, this.props.cardPadding, this.props.minCardWidth, this.props.maxAmountOfColumns);
      let currentRowNumber = 1;
      let cardsOnPage = 0;
      this.props.items.forEach((item: IItem) => {
        let content = item.content;
        if (!content) {
          content = "";
        }
        let div = document.createElement("div");
        div.innerHTML = content;
        let contentText = div.textContent || div.innerText || "";
        const showCommandBarItems = this.props.showCommandBarItems && this.props.userRole == "Administrator" || this.props.showCommandBarItems && this.props.userRole == "Editor" && item.author && this.props.user && item.author?.email?.toLowerCase() == this.props.user?.email?.toLowerCase();
        const likesEnabled = this.props.likesEnabled && !item.iaConfiguration?.disableLikes;
        const commentsEnabled = this.props.commentsEnabled && !item.iaConfiguration?.disableComments;

        const card = (
          <IACard
            key={`${item.id}_${item.startDate}`}
            id={item.id}
            token={this.props.token}
            tenant={this.props.tenant}
            environment={this.props.environment}
            type={this.props.type}
            instance={{ instanceName: this.props.instanceName }}
            user={this.props.user}
            tags={this.props.showCategories ? item.categories ? item.categories.map(category => { return category.name; }) : [] : undefined}
            tagsWithColor={this.props.showCategories ? item.categories ? item.categories : [] : undefined}
            title={this.props.showTitle ? (item.title ? item.title : "") : undefined}
            teaser={this.props.showTeaser ? contentText : undefined}
            author={this.props.showAuthor ? (item.author ? item.author.displayName : "") : undefined}
            fieldsToHide={item?.iaConfiguration?.fieldsToHide}
            date={this.props.showStartDate ? (item.startDate ? item.startDate : undefined) : undefined}
            isUnread={item.isUnread}
            fontSlots={this.props.fontSlots}
            userLocation={this.props.userLocation}
            cover={this.props.showImage ? {
              imageURL: item.image,
              iaMessageBgImagesFromBlob: item.iaMessageBgImagesFromBlob,
              videoURL: item.videoUrl,
              cropSettings: item.iaConfiguration?.cropSettings,
              modified: item && item.item ? item.item.modified : undefined
            } : undefined}
            eventSettings={this.props.type === "Event" ? {
              startDate: this.props.type === "Event" && item.startDate ? item.startDate : undefined,
              endDate: this.props.type === "Event" && item.endDate ? item.endDate : undefined,
              isAllDayEvent: item.isAllDayEvent,
              showTimespand: this.props.showTimespand,
              location: this.props.showLocation ? (item.location ? item.location : "") : undefined,
              displayDateOnCover: this.props.displayDateOnCover,
            } : undefined}
            likes={likesEnabled && this.props.showLikes ? item.likes : undefined}
            comments={commentsEnabled && this.props.showComments ? (item.commentCount != undefined ? item.commentCount : 0) : undefined}
            uniqueViews={this.props.showUniqueViews ? (item.uniqueViews != undefined ? item.uniqueViews : 0) : undefined}
            showEmojiPickerExpandButton={this.props.showEmojiPickerExpandButton}
            reactionOptions={this.props.reactionOptions}
            design={{
              textColor: this.props.textColor,
              highlightColor: this.props.highlightColor,
              borderColor: this.props.borderColor,
              backgroundColor: this.props.backgroundColor,
              unreadHighlightColor: this.props.unReadHighlight,
              showDropShadow: this.props.showDropShadow,
              borderRadius: this.props.borderRadius
            }}
            width={window.innerWidth < 667 ? window.innerWidth - 56 : maxCardWidthAvailable}
            isMobile={this.props.isMobile}
            isPanelOpen={this.props.isPanelOpen}
            styles={{
              marginRight: currentRowNumber == numberOfCardsPerRow ? 0 : 20,
              marginBottom: 20,
              width: window.innerWidth < 667 && this.props.isMobile ? "100%" : maxCardWidthAvailable
            }}
            onClickOpen={() => this.props.onClickOpen(item.item)}
            onClickEdit={showCommandBarItems && this.props.onClickEdit && !item.external ? () => this.props.onClickEdit(item.item) : undefined}
            onClickDelete={showCommandBarItems && this.props.onClickDelete && !item.external ? () => this.props.onClickDelete(item.item) : undefined}
            likesUpdated={(likes: ILike[]) => {
              this.props.likesUpdated(item.item, likes, item.index);
            }}
            commentsUpdated={(comments: number) => {
              this.props.commentsUpdated(item.item, comments, item.index);
            }}
            commentsClick={() => { this.props.commentsClick(item.item); }}
            labels={item.labels}
            alwaysShowFooter={this.props.showLikes || this.props.showComments || this.props.showUniqueViews}
            showPinIcon={item.isPinned}
            isSummaryDisabled={!this.props.showTeaser}
            showLikesDimmed={this.props.showLikesDimmed && !item.iaConfiguration?.disableLikes}
            showCommentsDimmed={this.props.showCommentsDimmed && !item.iaConfiguration?.isNewsLink && !item.iaConfiguration?.disableComments}
            showUniqueViewsDimmed={this.props.showUniqueViewsDimmed && this.dateTimeService.isOlderThanOneHour(item.startDate)}
            hideCommandBarButton={this.props.hideCommandBarButton}
            onClickCopy={showCommandBarItems && this.props.onClickCopy && !item.external ? () => this.props.onClickCopy(item.item) : undefined}
          />
        );
        messagesToDisplay.push(card);
        cardsOnPage++;
        if (currentRowNumber == numberOfCardsPerRow || cardsOnPage == this.props.numberOfCards) {
          currentRowNumber = 1;
        } else {
          currentRowNumber++;
        }
        if (cardsOnPage == this.props.numberOfCards) {
          cardsOnPage = 0;
        }
      });
    }

    return (
      <div
        style={{
          width: "100%",
          height: "auto"
        }}
      >
        {!this.props.loading && this.props.width != undefined &&
          messagesToDisplay
        }
        {this.props.loading && this.props.width != undefined &&
          this.skeletonCards()
        }
      </div>
    );
  }


}